// Who the daily briefing must NOT put on the rep's task list.
//
// The HubSpot mirror holds tasks against our own staff and test accounts —
// the onboarding contact at revailo.com, the Chicago Masonry sandbox — and
// every one of them was landing in "overdue or due today" beside real
// customers. A rep working the morning list cannot tell a test company from
// a prospect by name alone, so this is the list that says which e-mail
// addresses and domains are ours and are to be left off. The task node drops
// a task whose associated company sits at one of these domains (a task row
// carries company ids only, never a contact, so the domain IS the signal); the
// customer lanes apply the same list to the contact address they do carry.
//
// Stored per signed-in user in THIS browser, the house pattern for every
// overlay store (see copiedDrafts.js): there is no server-side settings
// store, so a rep who edits the list on their laptop will not see the edit on
// their phone — the Settings page says so in plain words.
//
// Two states that look alike but are different facts:
//   - NOTHING stored ("never set", or the value is unreadable) → the defaults
//     are in force and readExclusions() says isDefault: true.
//   - A stored EMPTY list → the rep chose "exclude nobody". That is honoured:
//     entries [] and isDefault false. Treating it as "never set" would put the
//     test accounts back on the list the moment the rep cleared it.
//
// Dropped rows are never dropped silently: judgeBoTasks() counts them and the
// briefing names the count and the domains in force.
import { currentScope } from './userScope.js'

export const DEFAULT_EXCLUSIONS = ['revailo.com', 'chicagomasonry.com']

// Broadcast name for live surfaces (Settings chips, the chat's "N hidden"
// line). Mirrors dailyTasks.js' 'revailosales:dailytasks'.
export const TASK_EXCLUSIONS_EVENT = 'revailosales:taskExclusions'

const BASE = 'revailosales.taskExclusions'
const listeners = new Set()

const key = (scope) => `${BASE}.${scope || 'anon'}`

// A domain is one or more non-empty dot-separated labels — at least one dot,
// no whitespace, no '@'. Deliberately loose about characters (a rep may paste
// a punycode or hyphenated host) and strict about shape, because a value
// without a dot ("revailo") would match nothing and sit on the list looking
// like protection it does not give.
// Labels are letters, digits, hyphens and underscores only (punycode is
// 'xn--…', still inside that), because the review found that a looser class
// let 'chicagomasonry.com;' and 'revailo.com:443' onto the list as chips that
// could never match a real company domain — protection that was not there.
const DOMAIN_RE = /^[a-z0-9_-]+(\.[a-z0-9_-]+)+$/

/**
 * One entry as the list stores and compares it: '' when refused, else a bare
 * lowercase domain ('revailo.com') or a lowercase address ('bo@revailo.com').
 *
 * Forgiving about how people paste — '@Revailo.com ', 'https://www.x.com/'
 * and 'Bo@Revailo.COM' all come out clean — but anything with inner spaces,
 * no dot, or more than one '@' is refused rather than guessed at, because a
 * guessed entry would silently exclude the wrong people (or nobody).
 */
export function normalizeExclusion(input) {
  let v = String(input ?? '')
    .trim()
    .toLowerCase()
  v = v.replace(/^https?:\/\//, '')
  v = v.replace(/^mailto:/, '')
  v = v.replace(/^@/, '')
  v = v.replace(/^www\./, '')
  // A pasted URL keeps only its HOST. The review caught the earlier version
  // storing 'chicagomasonry.com/contact-us' — a chip that looked like
  // protection and could never match a bare company_domain — so the path,
  // port, query and fragment are cut off here, before validation. Only for a
  // domain: an address keeps everything after its '@' intact for the check.
  if (!v.includes('@')) v = v.replace(/[\/:?#].*$/, '')
  if (!v || /\s/.test(v)) return ''
  const at = v.split('@')
  if (at.length > 2) return ''
  if (at.length === 2) {
    const [local, domain] = at
    if (!local || !DOMAIN_RE.test(domain)) return ''
    return `${local}@${domain}`
  }
  return DOMAIN_RE.test(v) ? v : ''
}

// Normalize + dedupe, keeping first-seen order so the Settings chips do not
// reshuffle under the rep's cursor after an add.
function clean(list) {
  const out = []
  const seen = new Set()
  for (const raw of Array.isArray(list) ? list : []) {
    const v = normalizeExclusion(raw)
    if (v && !seen.has(v)) {
      seen.add(v)
      out.push(v)
    }
  }
  return out
}

const sameSet = (a, b) => a.length === b.length && a.every((x) => b.includes(x))

// The stored list for the signed-in user, or null for "never set" (which
// includes an unreadable value — corrupt JSON or a non-array must fall back to
// the defaults, never throw into a render).
function readStored() {
  try {
    const raw = localStorage.getItem(key(currentScope()))
    if (raw === null) return null
    const parsed = JSON.parse(raw)
    return Array.isArray(parsed) ? clean(parsed) : null
  } catch {
    return null
  }
}

/** The list in force for the signed-in user: { entries, isDefault }. */
export function readExclusions() {
  const stored = readStored()
  if (stored === null) return { entries: [...DEFAULT_EXCLUSIONS], isDefault: true }
  // A stored list that happens to equal the defaults IS the defaults (see
  // write()), so Reset stays disabled and a future change to the defaults
  // reaches this rep — they never deviated from them.
  return { entries: stored, isDefault: sameSet(stored, DEFAULT_EXCLUSIONS) }
}

function notify() {
  const state = readExclusions()
  listeners.forEach((fn) => {
    try {
      fn(state)
    } catch {
      /* a bad subscriber must not break the others */
    }
  })
  try {
    window.dispatchEvent(new CustomEvent(TASK_EXCLUSIONS_EVENT, { detail: state }))
  } catch {
    /* SSR / no window */
  }
}

// Persist a list. A list equal to the defaults is stored as "never set"
// (key removed) rather than as an explicit copy: the two are the same choice,
// and keeping a copy would freeze this rep on today's defaults if the
// defaults are ever amended. An EMPTY list is stored as [] — that is the one
// choice that must survive as a stored value.
function write(entries) {
  try {
    const k = key(currentScope())
    if (sameSet(entries, DEFAULT_EXCLUSIONS)) localStorage.removeItem(k)
    else localStorage.setItem(k, JSON.stringify(entries))
  } catch {
    /* quota or private mode — the edit just won't persist past this tab */
  }
  notify()
}

/**
 * Add one address or domain. Returns the list now in force. A value the
 * normalizer refuses, or one already on the list, changes nothing and
 * notifies nobody — the caller decides what to tell the rep about it
 * (Settings shows the rejection inline).
 */
export function addExclusion(input) {
  const v = normalizeExclusion(input)
  const current = readExclusions().entries
  if (!v || current.includes(v)) return current
  const next = [...current, v]
  write(next)
  return next
}

/** Remove one entry (matched after normalization). Returns the list now in force. */
export function removeExclusion(entry) {
  const v = normalizeExclusion(entry)
  const current = readExclusions().entries
  if (!v || !current.includes(v)) return current
  const next = current.filter((x) => x !== v)
  write(next)
  return next
}

/** Back to DEFAULT_EXCLUSIONS: the stored key is deleted, not overwritten. */
export function resetExclusions() {
  try {
    localStorage.removeItem(key(currentScope()))
  } catch {
    /* nothing to remove, or storage unavailable — either way the defaults apply */
  }
  notify()
  return [...DEFAULT_EXCLUSIONS]
}

/** Fires with { entries, isDefault } after every write. Returns unsubscribe. */
export function subscribeExclusions(fn) {
  listeners.add(fn)
  return () => listeners.delete(fn)
}

/**
 * The list as two Sets — addresses ('bo@revailo.com') and domains
 * ('revailo.com') — built once per run so a node testing hundreds of rows
 * does not re-normalize the list per row.
 */
export function exclusionSet(entries) {
  const addresses = new Set()
  const domains = new Set()
  for (const v of clean(entries)) (v.includes('@') ? addresses : domains).add(v)
  return { addresses, domains }
}

/**
 * Why `value` (an e-mail address OR a bare domain) is excluded, as a clause
 * the receipt can print; '' when it is not. A domain matches exactly or as a
 * subdomain — mail.revailo.com is still ours — and the compare is
 * case-insensitive because HubSpot stores whatever the contact typed.
 */
export function exclusionReason(value, set) {
  if (!set) return ''
  const v = String(value ?? '')
    .trim()
    .toLowerCase()
  if (!v) return ''
  const at = v.lastIndexOf('@')
  if (at >= 0 && set.addresses?.has(v)) return `${v} is on the excluded list`
  const domain = at >= 0 ? v.slice(at + 1) : v
  if (!domain) return ''
  for (const d of set.domains || []) {
    if (domain === d || domain.endsWith(`.${d}`)) return `${v} is at ${d}, an excluded domain`
  }
  return ''
}

export function isExcluded(value, set) {
  return Boolean(exclusionReason(value, set))
}
